import React, { useEffect, useState, useRef } from 'react';
import PropTypes from 'prop-types';
import { useStoreId } from '@thd-nucleus/experience-context';
import { useSearchDeprecated, useRelatedSearchNavDeprecated } from '@thd-nucleus/data-sources/react/dataModel/migration';
import {
  useDataModel, arrayOf as arrayType, params, shape as shapeType, string as stringType
} from '@thd-nucleus/data-sources';
import { withHydrator } from '@thd-olt-component-react/hydrator';
import { RelatedSearchLinks } from './subcomponents/RelatedSearchLinks';
import { RelatedSearchProducts } from './subcomponents/RelatedSearchProducts';
import { PopularCategory } from './subcomponents/PopularCategory';
import { RecentSearch } from './subcomponents/RecentSearch'
import './related-search.scss';
import {withDynamicComponent} from "@thd-nucleus/app-render";
import {withLayoutGroup} from "@thd-olt-component-react/layout-manager";
import { RelatedSearchPlaceholder } from './RelatedSearchPlaceholder';



// @todo this should really be 2 components, one for search and one for product.
const RelatedSearchComponent = (props) => {
  const {
    itemId,
    keyword,
    navParam,
    recent,
    limitItems,
    noOfItems,
    storeId: defaultStoreId,
    isPopularCategory = false
  } = props;

  const {
    searchData: searchDataProps,
    searchError: searchErrorProps,
    searchLoading: searchLoadingProps
  } = props;

  let searchData;
  let searchError;
  let searchLoading;

  if (recent) {
    return <RecentSearch limitItems={limitItems} noOfItems={noOfItems} />
  }

  const storeId = defaultStoreId || useStoreId();
  const [loadedStoreId, setLoadedStoreId] = useState(() => storeId);

  const hasPropData = !!(searchDataProps || searchErrorProps || typeof searchLoadingProps !== 'undefined');
  ({
    data: searchData,
    error: searchError,
    loading: searchLoading
  } =  useSearchDeprecated({
    domains: ['relatedResults'],
    storeId,
    keyword,
    skip: isPopularCategory || ( !keyword || hasPropData),
    ssr: true
  }));

  if (hasPropData) {
    searchData = searchDataProps;
    searchError = searchErrorProps;
    searchLoading = searchLoadingProps;
  }

  const loadedStoreRef = useRef(storeId);
  const {
    data: navData,
    error: navError,
    loading: navLoading,
    variables,
  } = useRelatedSearchNavDeprecated({
    domains: ['relatedSearch', 'products'],
    productDomains: ['mediaSlim', 'identifiers', 'details'],
    navParam,
    storeId,
    itemId,
    skip: isPopularCategory || (!navParam && !itemId),
    ssr: true
  });

  
  const options = {
    variables: {},
    skip: !!itemId || !!navParam || !!keyword
  }
  const { data: popCatData, loading: popCatLoading, error: popCatError} = useDataModel('popularCategories', options)

  if (!navLoading && navData && variables.storeId) {
    if (variables.storeId !== loadedStoreId) {
      loadedStoreRef.current = variables.storeId;
    }
  }

  useEffect(() => { LIFE_CYCLE_EVENT_BUS.lifeCycle.trigger('related-search.ready'); }, []);

  if (searchLoading || navLoading || popCatLoading) {
      return (<RelatedSearchPlaceholder />);
  } 

  const searchDataIssue = ((searchError && !searchData) || !searchData || searchLoading) && (keyword);
  const navDataIssue = ((navError && !navData) || !navData || navLoading) && (navParam);
  const popCatIssue = ((popCatError && !popCatData) || !popCatData || popCatLoading) && isPopularCategory;

  const relatedSearchModel = searchData?.searchModel;
  const relatedNavModel = navData?.relatedSearchModel;
  const relatedSearchModelKeywords = !relatedSearchModel?.relatedResults?.relatedKeywords?.length ? null
    : relatedSearchModel?.relatedResults?.relatedKeywords;

  const relatedKeywords = relatedSearchModelKeywords || relatedNavModel?.relatedSearch;
  const relatedProducts = navData?.relatedSearchModel?.products || [];

  const popularCategories = popCatData?.popularCategories;

  if ((searchDataIssue || navDataIssue) || popCatIssue) {
    return null;
  }
  return (
    <div className="related-search">
      {
        !!relatedKeywords?.length
        && <RelatedSearchLinks relatedLinks={relatedKeywords} />
      }
      {
        !!relatedProducts?.length
        && <RelatedSearchProducts relatedSearchProducts={relatedProducts} />
      }
      {
        !!popularCategories?.length && <PopularCategory relatedLinks={popularCategories}/>
      }
    </div>
  );
};



RelatedSearchComponent.displayName = 'RelatedSearch';

const propTypes = {
  itemId: PropTypes.string,
  keyword: PropTypes.string,
  navParam: PropTypes.string,
  storeId: PropTypes.string,
  searchData: PropTypes.shape({}), // needs to be defined and removed from lint ignore
  searchError: PropTypes.shape({}),
  searchLoading: PropTypes.bool,
  recent: PropTypes.bool,
  limitItems: PropTypes.bool,
  noOfItems: PropTypes.number,
  isPopularCategory: PropTypes.bool
};
RelatedSearchComponent.propTypes = propTypes;

const defaultProps = {
  itemId: null,
  keyword: null,
  navParam: null,
  storeId: null,
  searchData: undefined,
  searchError: undefined,
  searchLoading: undefined,
  recent: false,
  limitItems: false,
  noOfItems: 10
};
RelatedSearchComponent.defaultProps = defaultProps;
const dataModel = {
  relatedSearchModel: params({ itemId: stringType(), navParam: stringType(), storeId: stringType() }).shape({
    products: arrayType(shapeType({
      itemId: stringType(),
      media: shapeType({
        images: arrayType(shapeType({
          url: stringType()
        }).client())
      }),
      identifiers: shapeType({
        canonicalUrl: stringType(),
        productLabel: stringType()
      }),
      details: shapeType({
        description: stringType()
      })
    })),
    relatedSearch: arrayType(shapeType({
      url: stringType(),
      anchor: stringType()
    }))
  }),
  popularCategories: arrayType(shapeType({
    text: stringType(),
    url: stringType()
  })),
  searchModel: params({ keyword: stringType(), storeId: stringType() }).shape({
    relatedResults: shapeType({
      relatedKeywords: arrayType(shapeType({
        keyword: stringType()
      }))
    })
  })
};

RelatedSearchComponent.dataModel = dataModel;

const HydratedRelatedSearch = withHydrator({
  id: 'related-search',
  scrollBuffer: 750,
  delay: 2000,
  preserveCtxVal: 'clientStore',
  placeholder: (
    <RelatedSearchPlaceholder
      color="#E0E0E0"
      showLoadingAnimation
    />
  )
}, RelatedSearchComponent);

const DynamicRelatedSearch = withDynamicComponent(HydratedRelatedSearch);
export const RelatedSearch = withLayoutGroup({ name: 'related-search'}, DynamicRelatedSearch);

RelatedSearch.dataModel = dataModel;
RelatedSearch.propTypes = propTypes;
RelatedSearch.defaultProps = defaultProps;
